import React from 'react';
import Popup from 'reactjs-popup';
import Flatpickr from 'react-flatpickr';
import { toastError } from '../../../../shared/toastHelper';
import usePledgeScheduleBuilder, { isEditableInstallment } from './usePledgeScheduleBuilder';
import PledgeCardSection from './PledgeCardSection';
import formatPledgeAmount from '../../../shared/utils/formatPledgeAmount';
import QuickCreateGivingModal from '../QuickCreateGivingModal';

const NEW_FUND_SENTINEL = '__new_fund__';
const NEW_CAMPAIGN_SENTINEL = '__new_campaign__';
const byName = (a, b) => a.name.localeCompare(b.name);

const fmt = (cents) => formatPledgeAmount(cents, { withDecimals: true });

function formatDueDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

const LOCKED_STATUS_LABELS = {
  paid: 'Paid',
  processing: 'Processing',
  canceled: 'Canceled',
};

const todayIso = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

export default function PledgeFormModal({
  mode, team, fan, organization, pledge, initialValues,
  previewSchedule, createPledge, updatePledge,
  onClose, onSaved,
}) {
  const [replaceResolverRef] = React.useState({ current: null });
  const [replaceOpen, setReplaceOpen] = React.useState(false);

  const confirmReplaceSchedule = React.useCallback(() => (
    new Promise((resolve) => {
      replaceResolverRef.current = resolve;
      setReplaceOpen(true);
    })
  ), [replaceResolverRef]);

  const resolveReplace = (value) => {
    if (replaceResolverRef.current) replaceResolverRef.current(value);
    replaceResolverRef.current = null;
    setReplaceOpen(false);
  };

  const {
    form, installments, pendingDeletions, scheduleSum, scheduleMatches, scheduleLocked,
    editInstallment, requestFormChange,
  } = usePledgeScheduleBuilder({
    initialPledge: pledge,
    initialValues,
    mode,
    previewSchedule,
    confirmReplace: confirmReplaceSchedule,
  });

  const [submitting, setSubmitting] = React.useState(false);
  const [savedPledge, setSavedPledge] = React.useState(pledge);
  const [awaitingCard, setAwaitingCard] = React.useState(false);
  const cardsAvailable = !!team.card_payments_available;

  const handlePledgeUpdated = (data) => {
    setSavedPledge(data);
    requestFormChange('lockVersion', data.lock_version);
  };
  const [errors, setErrors]         = React.useState({});
  const [funds, setFunds]           = React.useState(team.funds || []);
  const [campaigns, setCampaigns]   = React.useState(team.giving_campaigns || []);
  const [quickCreate, setQuickCreate] = React.useState(null);

  const handleFundChange = (value) => {
    if (value === NEW_FUND_SENTINEL) {
      setQuickCreate('fund');
      return;
    }
    requestFormChange('fundId', value ? Number(value) : null);
  };

  const handleCampaignChange = (value) => {
    if (value === NEW_CAMPAIGN_SENTINEL) {
      setQuickCreate('campaign');
      return;
    }
    requestFormChange('givingCampaignId', value ? Number(value) : null);
  };

  const handleQuickCreated = (record) => {
    if (quickCreate === 'fund') {
      setFunds((prev) => [...prev, record].sort(byName));
      requestFormChange('fundId', record.id);
    } else {
      setCampaigns((prev) => [...prev, record].sort(byName));
      requestFormChange('givingCampaignId', record.id);
    }
    setQuickCreate(null);
  };

  const handleSubmit = async () => {
    if (!scheduleMatches) {
      setErrors({ base: ['Schedule must sum to total pledge amount'] });
      return;
    }
    setSubmitting(true);
    try {
      const attrs = {
        fan_id: fan?.id,
        organization_id: organization?.id,
        fund_id: form.fundId,
        giving_campaign_id: form.givingCampaignId,
        appeal_id: form.appealId,
        solicitor_team_membership_id: form.solicitorId,
        name: form.name,
        total_amount_cents: form.totalAmountCents,
        pledge_date: form.pledgeDate,
        expected_fulfillment_date: form.expectedFulfillmentDate,
        installment_frequency: form.installmentFrequency,
        installments_count: form.installmentsCount,
        first_installment_due_on: form.firstInstallmentDueOn,
        notes: form.notes,
        terms_and_conditions: form.termsAndConditions,
        cover_processing: form.coverProcessing,
        lock_version: form.lockVersion,
        installments_attributes: [
          ...installments.map((row) => ({
            id: row.id, sequence: row.sequence,
            due_date: row.due_date, amount_cents: row.amount_cents,
          })),
          ...(pendingDeletions || []),
        ],
      };
      if (mode === 'create') {
        const created = await createPledge(attrs);
        if (cardsAvailable) {
          setSavedPledge(created);
          setAwaitingCard(true);
          return;
        }
      } else {
        await updatePledge(pledge.id, attrs);
      }
      onSaved();
    } catch (e) {
      if (e?.response?.status === 422) {
        setErrors(e.response.data.errors || {});
      } else if (e?.response?.status === 409) {
        toastError('This pledge was modified by someone else. Reload and try again.');
        onClose();
      } else {
        toastError('Could not save pledge. Refresh and try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const frequencyOptions = [
    { value: 'one_time',      label: 'One-time' },
    { value: 'monthly',       label: 'Monthly' },
    { value: 'quarterly',     label: 'Quarterly' },
    { value: 'semi_annually', label: 'Semi-Annually' },
    { value: 'annually',      label: 'Annually' },
  ];

  const titleText = mode === 'create' ? 'New pledge' : `Edit pledge — ${pledge?.name}`;
  const subtitleText = organization
    ? `For ${organization.name}`
    : `For ${fan.first_name} ${fan.last_name} · ${fan.email}`;

  if (awaitingCard) {
    return (
      <div className="cfm-overlay">
        <div className="cfm-modal" role="dialog" aria-label="Add payment card">
          <div className="cfm-header">
            <div className="pfm-header-text">
              <div className="pfm-title">Add payment card</div>
              <div className="pfm-subtitle">{subtitleText}</div>
            </div>
            <button className="cfm-close" onClick={onSaved} aria-label="Close">&times;</button>
          </div>
          <div className="cfm-body">
            <div className="cfm-pane-main">
              <p className="setlist-text-sm">
                Pledge created. Enter the card the donor gave you and Opendate will charge each installment due
                from today onward on its due date. You can also skip this and add the card later.
              </p>
              <PledgeCardSection teamId={team.id} pledge={savedPledge} onPledgeUpdated={setSavedPledge} />
            </div>
          </div>
          <div className="cfm-footer">
            <div className="pfm-footer-actions">
              <button type="button" className="btn btn-setlist-primary" onClick={onSaved}>
                {savedPledge.card ? 'Done' : 'Skip'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cfm-overlay">
      <div className="cfm-modal" role="dialog" aria-label={titleText}>

        {/* Header */}
        <div className="cfm-header">
          <div className="pfm-header-text">
            <div className="pfm-title">{titleText}</div>
            <div className="pfm-subtitle">{subtitleText}</div>
          </div>
          <button className="cfm-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        {/* Errors (all fields, flat list) */}
        {Object.keys(errors).length > 0 && (
          <div className="cfm-errors">
            <ul>
              {Object.entries(errors).flatMap(([field, msgs]) => {
                const list = Array.isArray(msgs) ? msgs : [msgs];
                const prefix = field === 'base' ? '' : `${field.replace(/_/g, ' ')}: `;
                return list.map((msg, i) => (
                  <li key={`${field}-${i}`}>{prefix}{msg}</li>
                ));
              })}
            </ul>
          </div>
        )}

        {/* Body */}
        <div className="cfm-body">

          {/* Left pane — form fields */}
          <div className="cfm-pane-main">

            <div className="cfm-field-row-2col">
              <div className="cfm-field">
                <label className="cfm-label">
                  Pledge name <span className="required">*</span>
                </label>
                <div className="cfm-input-wrap">
                  <input
                    className="cfm-input"
                    type="text"
                    value={form.name || ''}
                    placeholder="e.g. Founding Partner – 3 year"
                    onChange={(e) => requestFormChange('name', e.target.value)}
                  />
                </div>
                <div className="text-muted small">What the donor is committing to. Use Campaign for the drive it counts toward.</div>
              </div>
              <div className="cfm-field">
                <label className="cfm-label">Campaign</label>
                <div className="cfm-select-wrap">
                  <select
                    className="cfm-select"
                    value={form.givingCampaignId || ''}
                    onChange={(e) => handleCampaignChange(e.target.value)}
                  >
                    <option value="">None</option>
                    {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    <option value={NEW_CAMPAIGN_SENTINEL}>+ Create new campaign…</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Row 1: Total pledge amount | Pledge date */}
            <div className="cfm-field-row-2col">
              <div className="cfm-field">
                <label className="cfm-label">
                  Total pledge amount <span className="required">*</span>
                </label>
                <div className="cfm-input-wrap">
                  <input
                    className="cfm-input"
                    type="number"
                    value={Number(form.totalAmountCents) / 100}
                    onChange={(e) => requestFormChange('totalAmountCents', Math.round(Number(e.target.value) * 100))}
                  />
                </div>
              </div>
              <div className="cfm-field">
                <label className="cfm-label">
                  Pledge date <span className="required">*</span>
                </label>
                <div className="cfm-input-wrap">
                  <Flatpickr
                    className="cfm-input"
                    value={form.pledgeDate || ''}
                    options={{ dateFormat: 'Y-m-d', allowInput: true }}
                    onChange={(_, dateStr) => requestFormChange('pledgeDate', dateStr || '')}
                  />
                </div>
              </div>
            </div>

            {/* Row 2: Fund / Designation | Expected fulfillment */}
            <div className="cfm-field-row-2col">
              <div className="cfm-field">
                <label className="cfm-label">Fund / Designation</label>
                <div className="cfm-select-wrap">
                  <select
                    className="cfm-select"
                    value={form.fundId || ''}
                    onChange={(e) => handleFundChange(e.target.value)}
                  >
                    <option value="">None</option>
                    {funds.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                    <option value={NEW_FUND_SENTINEL}>+ Create new fund…</option>
                  </select>
                </div>
              </div>
              <div className="cfm-field">
                <label className="cfm-label">
                  Expected fulfillment <span className="required">*</span>
                </label>
                <div className="cfm-input-wrap">
                  <Flatpickr
                    className="cfm-input"
                    value={form.expectedFulfillmentDate || ''}
                    options={{ dateFormat: 'Y-m-d', allowInput: true }}
                    onChange={(_, dateStr) => requestFormChange('expectedFulfillmentDate', dateStr || '')}
                  />
                </div>
              </div>
            </div>

            <div className="cfm-field-row-2col">
              <div className="cfm-field">
                <label className="cfm-label">Solicitor</label>
                <div className="cfm-select-wrap">
                  <select
                    className="cfm-select"
                    value={form.solicitorId || ''}
                    onChange={(e) => requestFormChange('solicitorId', e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">None</option>
                    {team.members?.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Row 4: Installment frequency (full width) */}
            <div className="cfm-field">
              <label className="cfm-label">
                Installment frequency <span className="required">*</span>
              </label>
              <div className="pfm-segmented">
                {frequencyOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`pfm-segmented__btn${form.installmentFrequency === opt.value ? ' pfm-segmented__btn--active' : ''}`}
                    disabled={scheduleLocked}
                    onClick={() => requestFormChange('installmentFrequency', opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Row 5: Number of installments | First installment due */}
            <div className="cfm-field-row-2col">
              <div className="cfm-field">
                <label className="cfm-label">Number of installments</label>
                <div className="cfm-input-wrap">
                  <input
                    className="cfm-input"
                    type="number"
                    min="1"
                    value={form.installmentsCount}
                    disabled={scheduleLocked || form.installmentFrequency === 'one_time'}
                    onChange={(e) => requestFormChange('installmentsCount', Number(e.target.value))}
                  />
                </div>
              </div>
              <div className="cfm-field">
                <label className="cfm-label">First installment due</label>
                <div className="cfm-input-wrap">
                  <Flatpickr
                    className="cfm-input"
                    value={form.firstInstallmentDueOn || ''}
                    options={{ dateFormat: 'Y-m-d', allowInput: !scheduleLocked, clickOpens: !scheduleLocked }}
                    onChange={(_, dateStr) => requestFormChange('firstInstallmentDueOn', dateStr || '')}
                  />
                </div>
                <div className="pfm-schedule-hint">Can be later than the pledge date, e.g. after the donor's next payday.</div>
              </div>
            </div>
            {scheduleLocked && (
              <div className="pfm-schedule-hint">
                Some installments have already been charged or canceled, so the frequency, number of installments and first due date
                are locked. Change the remaining installments' amounts and due dates, or the total, instead.
              </div>
            )}

            {cardsAvailable && (
              <div className="cfm-field">
                <label className="cfm-label">Payment card</label>
                {mode === 'edit' ? (
                  <PledgeCardSection teamId={team.id} pledge={savedPledge} onPledgeUpdated={handlePledgeUpdated} />
                ) : (
                  <div className="pfm-schedule-hint">You can enter the donor's card after creating the pledge.</div>
                )}
                <label className="d-flex align-items-center mt-2" style={{ gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={form.coverProcessing}
                    onChange={(e) => requestFormChange('coverProcessing', e.target.checked)}
                  />
                  Donor covers processing fees
                </label>
              </div>
            )}

          </div>{/* /cfm-pane-main */}

          {/* Right pane — schedule preview */}
          <div className="pfm-pane-schedule">
            <div className="pfm-schedule-header">Schedule preview</div>

            {installments.length === 0 ? (
              <div className="pfm-schedule-empty">
                Enter a total amount and frequency to preview the schedule.
              </div>
            ) : (
              <table className="pfm-schedule-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Due date</th>
                    <th className="pfm-col-amount">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {installments.map((row, i) => (
                    <tr key={i}>
                      <td>{row.sequence}</td>
                      {isEditableInstallment(row) ? (
                        <>
                          <td>
                            <input
                              type="date"
                              className="pfm-date-input"
                              aria-label={`Installment ${row.sequence} due date`}
                              required
                              min={todayIso()}
                              value={row.due_date || ''}
                              onChange={(e) => { if (e.target.value && e.target.value >= todayIso()) editInstallment(i, { due_date: e.target.value }); }}
                            />
                          </td>
                          <td>
                            <div className="pfm-amount-input">
                              <span className="pfm-amount-input__prefix">$</span>
                              <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                aria-label={`Installment ${row.sequence} amount`}
                                value={Number(row.amount_cents) / 100}
                                onChange={(e) => {
                                  const dollars = Number(e.target.value);
                                  if (Number.isFinite(dollars) && dollars >= 0) editInstallment(i, { amount_cents: Math.round(dollars * 100) });
                                }}
                              />
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>{formatDueDate(row.due_date)}</td>
                          <td className="pfm-locked-amount">
                            {fmt(row.charged_amount_cents || row.amount_cents)}
                            <span className="pfm-locked-status">{LOCKED_STATUS_LABELS[row.derived_status]}</span>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="pfm-schedule-total">
              <span>Scheduled total</span>
              <span className={`pfm-schedule-total__amount${scheduleMatches ? '' : ' pfm-schedule-total__amount--error'}`}>
                {fmt(scheduleSum)}
              </span>
            </div>

            <div className="pfm-schedule-hint">
              Edit any amount for step-up or irregular gifts. The schedule must sum to the total pledge amount.
            </div>
          </div>{/* /pfm-pane-schedule */}

        </div>{/* /cfm-body */}

        {/* Footer */}
        <div className="cfm-footer">
          <div className="pfm-footer-actions">
            <button
              type="button"
              className="btn btn-setlist-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-setlist-primary"
              disabled={submitting || !scheduleMatches}
              onClick={handleSubmit}
            >
              {mode === 'create' ? 'Create pledge' : 'Save changes'}
            </button>
          </div>
        </div>

      </div>{/* /cfm-modal */}

      {quickCreate && (
        <QuickCreateGivingModal
          kind={quickCreate}
          teamId={team.id}
          onCreated={handleQuickCreated}
          onClose={() => setQuickCreate(null)}
        />
      )}

      <Popup
        open={replaceOpen}
        onClose={() => resolveReplace(false)}
        modal
        closeOnDocumentClick
        contentStyle={{
          border: '1px solid var(--sl-border-secondary)',
          borderRadius: '8px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          width: '420px',
          padding: '24px',
        }}
      >
        <h2 className="setlist-text-lg setlist-font-semibold mb-2">Replace installment schedule?</h2>
        <p className="text-muted setlist-text-sm mb-3">
          This will replace your installment schedule with a fresh auto-generated one.
        </p>
        <div className="d-flex gap-2 justify-content-end">
          <button className="btn btn-setlist-secondary" onClick={() => resolveReplace(false)}>
            Keep current schedule
          </button>
          <button className="btn btn-setlist-primary" onClick={() => resolveReplace(true)}>
            Replace
          </button>
        </div>
      </Popup>
    </div>
  );
}
